import React, { useEffect, useMemo, useRef, useState } from "react";
import { compact, fmt } from "./format";

/**
 * Charts drawn as plain SVG at the container's real pixel width (no
 * stretched viewBox, so text and strokes stay crisp). Mark specs follow the
 * dataviz method: thin marks, a 2px surface gap between stacked segments,
 * rounded data-ends, recessive grid, a crosshair or per-mark tooltip on
 * every plot, and a legend for two or more series.
 */

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    setWidth(Math.floor(ref.current.getBoundingClientRect().width));
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** A round axis top: 7 → 8, 34 → 40, 230 → 250. */
export function niceMax(v) {
  if (!v || v <= 0) return 4;
  if (v >= 1 && v <= 4) return 4;
  const p = 10 ** Math.floor(Math.log10(v));
  return [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((m) => m * p).find((m) => m >= v);
}

// ── Sparkline ──────────────────────────────────────────────────────────────

export function Sparkline({ values, color = "var(--aa-s1)" }) {
  const W = 96;
  const H = 34;
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const pts = values.map((v, i) => [(i / (values.length - 1)) * W, H - 3 - ((v - min) / span) * (H - 6)]);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
  const id = useMemo(() => `sg${Math.random().toString(36).slice(2, 8)}`, []);
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line}L${W},${H}L0,${H}Z`} fill={`url(#${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.75" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

// ── Legend ─────────────────────────────────────────────────────────────────

export function Legend({ series, hidden, onToggle }) {
  return (
    <div className="aa-legend">
      {series.map((s) => (
        <button key={s.key} type="button" aria-pressed={!hidden?.has(s.key)} onClick={() => onToggle?.(s.key)} title={onToggle ? "Show or hide" : undefined}>
          <span className="aa-swatch" style={{ background: s.color }} />
          {s.label}
        </button>
      ))}
    </div>
  );
}

function Tooltip({ x, width, children }) {
  const left = x < width * 0.6;
  return (
    <div className="aa-tip" style={{ top: 4, ...(left ? { left: x + 14 } : { right: width - x + 14 }) }}>
      {children}
    </div>
  );
}

function YAxis({ max, plotW, y, left, format = compact }) {
  const ticks = [0, max / 4, max / 2, (3 * max) / 4, max];
  return ticks.map((t) => (
    <g key={t}>
      <line x1={left} x2={left + plotW} y1={y(t)} y2={y(t)} stroke={t ? "var(--aa-grid)" : "var(--aa-axis)"} strokeDasharray={t ? "2 4" : undefined} />
      <text x={left - 8} y={y(t) + 4} fontSize={11} fill="var(--aa-faint)" textAnchor="end" className="aa-num">
        {format(t)}
      </text>
    </g>
  ));
}

// ── Stacked columns ────────────────────────────────────────────────────────

/**
 * One stacked column per day. `rows` carry `key`, `label` (axis), `long`
 * (tooltip title) and one number per series key. Legend toggles hide a
 * series; clicking a segment calls `onSelect(row, seriesKey)`.
 */
export function StackedBars({ rows, series, height = 260, onSelect, footer, format = fmt, axisFormat = compact }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const [hidden, setHidden] = useState(new Set());
  const visible = series.filter((s) => !hidden.has(s.key));
  const toggle = (k) => setHidden((h) => {
    const next = new Set(h);
    next.has(k) ? next.delete(k) : next.add(k);
    return next.size === series.length ? h : next;
  });

  const left = 40;
  const right = 4;
  const top = 10;
  const bottom = 26;
  const plotW = Math.max(0, width - left - right);
  const plotH = height - top - bottom;
  const totals = rows.map((r) => visible.reduce((s, x) => s + (r[x.key] || 0), 0));
  const max = niceMax(Math.max(0, ...totals));
  const slot = rows.length ? plotW / rows.length : 0;
  const bw = Math.max(2, Math.min(36, slot * 0.68));
  const y = (v) => top + plotH - (v / max) * plotH;
  const labelEvery = Math.max(1, Math.ceil(rows.length / Math.max(1, Math.floor(plotW / 64))));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <Legend series={series} hidden={hidden} onToggle={toggle} />
      </div>
      <div ref={ref} style={{ position: "relative", height }} onMouseLeave={() => setHover(null)}>
        {width > 0 && (
          <svg width={width} height={height} style={{ display: "block", overflow: "visible" }}>
            <YAxis max={max} plotW={plotW} y={y} left={left} format={axisFormat} />
            {rows.map((r, i) => {
              const x = left + i * slot + (slot - bw) / 2;
              let acc = 0;
              const parts = visible.filter((s) => r[s.key] > 0);
              return (
                <g key={r.key} onMouseEnter={() => setHover(i)}>
                  <rect x={left + i * slot} y={top} width={slot} height={plotH} fill={hover === i ? "var(--aa-hover)" : "transparent"} rx={4} />
                  {parts.map((s, j) => {
                    const v = r[s.key];
                    const y1 = y(acc);
                    const y0 = y(acc + v);
                    acc += v;
                    const last = j === parts.length - 1;
                    // A 2px surface gap between segments; the top segment carries the rounded end.
                    const h = Math.max(1, y1 - y0 - (j > 0 ? 2 : 0));
                    const r0 = last ? Math.min(3, bw / 2, h) : 0;
                    return (
                      <path
                        key={s.key}
                        d={roundedTop(x, y0, bw, h, r0)}
                        fill={s.color}
                        style={{ cursor: onSelect ? "pointer" : "default" }}
                        onClick={() => onSelect?.(r, s.key)}
                      />
                    );
                  })}
                  {i % labelEvery === 0 && (
                    <text x={left + i * slot + slot / 2} y={height - 6} fontSize={11} fill="var(--aa-faint)" textAnchor="middle">
                      {r.label}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        )}
        {hover != null && totals[hover] > 0 && (
          <Tooltip x={left + hover * slot + slot / 2} width={width}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{rows[hover].long}</div>
            {[...visible].reverse().map((s) => (
              <div key={s.key} className="aa-tip-row" style={{ color: rows[hover][s.key] ? "var(--aa-ink)" : "var(--aa-faint)" }}>
                <span className="aa-swatch" style={{ background: s.color }} />
                {s.label}
                <b>{format(rows[hover][s.key] ?? 0)}</b>
              </div>
            ))}
            <div className="aa-tip-row" style={{ borderTop: "1px solid var(--aa-border)", marginTop: 6, paddingTop: 6 }}>
              Total
              <b>{format(totals[hover])}</b>
            </div>
            {footer?.(rows[hover])}
            {onSelect && <div style={{ color: "var(--aa-faint)", marginTop: 6 }}>Click a segment to filter to it</div>}
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function roundedTop(x, y, w, h, r) {
  if (!r) return `M${x},${y}h${w}v${h}h${-w}Z`;
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

// ── Lines ──────────────────────────────────────────────────────────────────

/**
 * Lines over time with a crosshair tooltip. The first series gets a soft
 * area; `format` prints values; `max` pins the axis (1 for rates).
 */
export function LineChart({ rows, series, height = 220, format = fmt, axisFormat = compact, max: fixedMax, onSelect }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const left = 40;
  const right = 8;
  const top = 10;
  const bottom = 26;
  const plotW = Math.max(0, width - left - right);
  const plotH = height - top - bottom;
  const max = fixedMax ?? niceMax(Math.max(0, ...rows.flatMap((r) => series.map((s) => r[s.key] ?? 0))));
  const x = (i) => left + (rows.length <= 1 ? plotW / 2 : (i / (rows.length - 1)) * plotW);
  const y = (v) => top + plotH - (v / max) * plotH;
  const labelEvery = Math.max(1, Math.ceil(rows.length / Math.max(1, Math.floor(plotW / 64))));
  const id = useMemo(() => `lg${Math.random().toString(36).slice(2, 8)}`, []);

  const pathOf = (key) => {
    let d = "";
    let pen = false;
    rows.forEach((r, i) => {
      const v = r[key];
      if (v == null) {
        pen = false;
        return;
      }
      d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
      pen = true;
    });
    return d;
  };

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left - left;
    const i = rows.length <= 1 ? 0 : Math.round((px / plotW) * (rows.length - 1));
    setHover(Math.max(0, Math.min(rows.length - 1, i)));
  };

  return (
    <div>
      {series.length > 1 && (
        <div style={{ marginBottom: 8 }}>
          <Legend series={series} />
        </div>
      )}
      <div ref={ref} style={{ position: "relative", height }} onMouseLeave={() => setHover(null)}>
        {width > 0 && (
          <svg width={width} height={height} style={{ display: "block", overflow: "visible" }} onMouseMove={onMove} onClick={() => hover != null && onSelect?.(rows[hover])}>
            <defs>
              <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={series[0]?.color} stopOpacity="0.18" />
                <stop offset="100%" stopColor={series[0]?.color} stopOpacity="0" />
              </linearGradient>
            </defs>
            <YAxis max={max} plotW={plotW} y={y} left={left} format={axisFormat} />
            {series[0] && rows.length > 1 && <path d={`${pathOf(series[0].key)}L${x(rows.length - 1)},${y(0)}L${x(0)},${y(0)}Z`} fill={`url(#${id})`} />}
            {series.map((s) => (
              <path key={s.key} d={pathOf(s.key)} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" strokeDasharray={s.dashed ? "4 4" : undefined} />
            ))}
            {rows.map(
              (r, i) =>
                i % labelEvery === 0 && (
                  <text key={r.key} x={x(i)} y={height - 6} fontSize={11} fill="var(--aa-faint)" textAnchor="middle">
                    {r.label}
                  </text>
                ),
            )}
            {hover != null && (
              <g pointerEvents="none">
                <line x1={x(hover)} x2={x(hover)} y1={top} y2={top + plotH} stroke="var(--aa-border-strong)" />
                {series.map(
                  (s) =>
                    rows[hover][s.key] != null && (
                      <circle key={s.key} cx={x(hover)} cy={y(rows[hover][s.key])} r={4.5} fill={s.color} stroke="var(--aa-surface)" strokeWidth={2} />
                    ),
                )}
              </g>
            )}
            <rect x={left} y={top} width={plotW} height={plotH} fill="transparent" style={{ cursor: onSelect ? "pointer" : "crosshair" }} />
          </svg>
        )}
        {hover != null && (
          <Tooltip x={x(hover)} width={width}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{rows[hover].long ?? rows[hover].label}</div>
            {series.map((s) => (
              <div key={s.key} className="aa-tip-row">
                <span className="aa-swatch" style={{ background: s.color, height: 3, borderRadius: 2 }} />
                {s.label}
                <b>{rows[hover][s.key] == null ? "—" : format(rows[hover][s.key])}</b>
              </div>
            ))}
          </Tooltip>
        )}
      </div>
    </div>
  );
}

// ── Bar list (ranked breakdown) ────────────────────────────────────────────

/**
 * A ranked list with the bar drawn behind the label — the breakdown form
 * analytics tools use for "top N". Rows with `onClick` filter or drill.
 */
/**
 * `stacked` puts the bar under the label instead of behind it — for lists where
 * the label is a sentence and a short bar would stop mid-word. `selectedKey`
 * marks the row whose detail is open elsewhere on the card.
 */
export function BarList({ rows, format = fmt, color = "var(--aa-s1)", limit, valueLabel, labelHeader, stacked = false, selectedKey }) {
  const [all, setAll] = useState(false);
  const max = Math.max(1, ...rows.map((r) => r.value || 0));
  const shown = limit && !all ? rows.slice(0, limit) : rows;
  return (
    <div>
      {(valueLabel || labelHeader) && (
        <div style={{ display: "flex", fontSize: 11, color: "var(--aa-faint)", fontWeight: 500, marginBottom: 6, padding: "0 0 0 10px" }}>
          <span style={{ flex: 1 }}>{labelHeader}</span>
          <span style={{ width: 92, textAlign: "right" }}>{valueLabel}</span>
        </div>
      )}
      <div style={{ display: "grid", gap: 4 }}>
        {shown.map((r) => (
          <div
            key={r.key ?? r.label}
            className="aa-barlist-row"
            data-click={Boolean(r.onClick)}
            data-stacked={stacked || undefined}
            data-selected={selectedKey != null && (r.key ?? r.label) === selectedKey ? "true" : undefined}
            onClick={r.onClick}
            title={r.title ?? (typeof r.label === "string" ? r.label : undefined)}
          >
            <div className="aa-barlist-track">
              <div
                className="aa-barlist-fill"
                style={{ width: `${Math.max(1.5, ((r.value || 0) / max) * 100)}%`, background: r.color ?? color, opacity: stacked ? 0.55 : 0.16 }}
              />
              <div className="aa-barlist-label">{r.label}</div>
            </div>
            <div className="aa-barlist-value">
              {format(r.value)}
              {r.note && <span style={{ color: "var(--aa-faint)", fontWeight: 400, marginLeft: 6 }}>{r.note}</span>}
            </div>
          </div>
        ))}
      </div>
      {limit && rows.length > limit && (
        <button type="button" className="aa-link" style={{ fontSize: 12, marginTop: 10, marginLeft: 10 }} onClick={() => setAll((a) => !a)}>
          {all ? "Show less" : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

// ── Donut ──────────────────────────────────────────────────────────────────

export function Donut({ segments, size = 148, thickness = 18, center, sub }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  const gap = segments.filter((s) => s.value).length > 1 ? 2 : 0;
  return (
    <div style={{ position: "relative", width: size, height: size, flex: "none" }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--aa-surface-3)" strokeWidth={thickness} />
        {total > 0 &&
          segments.map((s) => {
            const len = ((s.value || 0) / total) * c;
            const el = len > 0 && (
              <circle
                key={s.label}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={thickness}
                strokeDasharray={`${Math.max(0, len - gap)} ${c}`}
                strokeDashoffset={-offset}
              >
                <title>{`${s.label}: ${fmt(s.value)}`}</title>
              </circle>
            );
            offset += len;
            return el;
          })}
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 22, fontWeight: 600 }} className="aa-num">
          {center}
        </div>
        {sub && <div style={{ fontSize: 11, color: "var(--aa-muted)" }}>{sub}</div>}
      </div>
    </div>
  );
}

/** A donut with its legend as a value list beside it. */
export function DonutBreakdown({ segments, center, sub, format = fmt }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
      <Donut segments={segments} center={center} sub={sub} />
      <div style={{ flex: 1, minWidth: 200, display: "grid", gap: 2 }}>
        {segments.map((s) => (
          <div
            key={s.label}
            className="aa-barlist-row"
            data-click={Boolean(s.onClick)}
            onClick={s.onClick}
            style={{ height: "auto", padding: "6px 8px", gap: 10, alignItems: "flex-start" }}
            title={s.help}
          >
            <span className="aa-swatch" style={{ background: s.color, marginTop: 4 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="aa-barlist-label" style={{ padding: 0, height: "auto", display: "block" }}>
                {s.label}
              </span>
              {s.help && <span className="aa-sub">{s.help}</span>}
            </span>
            <span className="aa-num" style={{ fontWeight: 600, fontSize: 13 }}>
              {format(s.value)}
            </span>
            <span className="aa-num" style={{ width: 44, textAlign: "right", fontSize: 12, color: "var(--aa-muted)" }}>
              {total ? `${Math.round(((s.value || 0) / total) * 100)}%` : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Proportion bar ─────────────────────────────────────────────────────────

/** A single 100% bar split into parts — a compact "how did these end" figure. */
export function SegmentBar({ segments, height = 8 }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  if (!total) return <div style={{ height, borderRadius: height, background: "var(--aa-surface-3)" }} />;
  return (
    <div style={{ display: "flex", gap: 2, height }}>
      {segments
        .filter((s) => s.value > 0)
        .map((s, i, arr) => (
          <div
            key={s.label}
            title={`${s.label}: ${fmt(s.value)} (${Math.round((s.value / total) * 100)}%)`}
            style={{
              width: `${(s.value / total) * 100}%`,
              background: s.color,
              borderRadius: `${i === 0 ? height : 0}px ${i === arr.length - 1 ? height : 0}px ${i === arr.length - 1 ? height : 0}px ${i === 0 ? height : 0}px`,
              minWidth: 3,
            }}
          />
        ))}
    </div>
  );
}

/** A thin progress meter for a rate in a table cell. */
export function Meter({ value, color = "var(--aa-s1)", width = 64 }) {
  return (
    <span style={{ display: "inline-block", width, height: 6, borderRadius: 3, background: "var(--aa-surface-3)", verticalAlign: "middle", overflow: "hidden" }}>
      <span style={{ display: "block", height: "100%", width: `${Math.max(0, Math.min(1, value ?? 0)) * 100}%`, background: color, borderRadius: 3 }} />
    </span>
  );
}
